import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FolderPlus, Link2, Plus, Sparkles, UploadCloud } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { GoogleDriveIcon } from "@/components/icons/google-drive-icon";
import { FolderNameDialog } from "./folder-name-dialog";
import { useHasFeature } from "@/hooks/use-plan";

type Props = {
  uploadHref: string;
  /** Async — receives the trimmed name and creates the folder via the mutation. */
  onCreateFolder: (name: string) => void | Promise<void>;
};

/**
 * "Upload Asset" and "Upload Folder" both land on the REAL upload page
 * (src/pages/upload.tsx) — "Upload Folder" adds &mode=folder, which flips
 * the file input to accept a whole OS folder (webkitdirectory) instead of
 * individual files. Bulk-folder ingestion into a real backend folder tree
 * is still frontend-only (2026-07-14) — the uploaded files land in the
 * current project/folder scope as flat assets. "New Folder" IS backed by
 * migration 0012.
 *
 * "Import from Google Drive" (2026-08-21) adds &drive=1, which auto-opens the
 * Google Picker on the upload page; picked files download and enter the SAME
 * upload queue as local files. Same project/folder scope carried in uploadHref.
 */
export function UploadMenu({ uploadHref, onCreateFolder }: Props) {
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const folderUploadHref = `${uploadHref}&mode=folder`;
  const driveUploadHref = `${uploadHref}&drive=1`;
  const addLinkHref = uploadHref.replace("/upload", "/add-link");
  // Google Drive import is a paid feature (Starter+). Free is soft-locked to the
  // Plan page. Server can't gate it (client-side Picker), so this is the only gate.
  const canDrive = useHasFeature("googleDriveImport");
  const { pathname } = useLocation();
  const planHref = pathname.startsWith("/admin") ? "/admin/settings/plan" : "/app/settings/plan";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Add">
            <Plus className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem asChild>
            <Link to={uploadHref}>
              <UploadCloud />
              Upload Asset
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={folderUploadHref}>
              <UploadCloud />
              Upload Folder
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={canDrive ? driveUploadHref : planHref}>
              <GoogleDriveIcon className="h-4 w-4" />
              Import from Google Drive
              {!canDrive && (
                <span className="ml-auto flex items-center gap-1 text-[10px] font-medium text-primary">
                  <Sparkles className="h-3 w-3" strokeWidth={2} />
                  Starter
                </span>
              )}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={addLinkHref}>
              <Link2 />
              Add from Link
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setFolderDialogOpen(true)}>
            <FolderPlus />
            New Folder
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <FolderNameDialog
        mode="create"
        open={folderDialogOpen}
        onOpenChange={setFolderDialogOpen}
        onSubmit={onCreateFolder}
      />
    </>
  );
}
